import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../common/Icon';
import CarInfoBox from '../common/CarInfoBox';
import ReviewButton from '../common/ReviewButton';
import ReviewListModal from './ReviewListModal';
import { reviewService } from '../../api/services';
import { useDeepLTranslated } from '../../hooks/useDeepLTranslated';
import { useMoney } from '../../hooks/useMoney';

/**
 * VehicleInfoSection: 이미지 + 차량정보 + 후기버튼 + 요금관련안내
 *
 * Props:
 *   CarInfoBox props: images, carName, usageTimeBadge, specs

 *   waitInfo: { overtimePrice?, overtimeI18nKey?, overtime?, freeRows? }
 *     - overtimePrice: 초과 요금(KRW, raw number) — useMoney 로 통화 변환해 표시
 *     - overtimeI18nKey: 라벨 i18n 키 (airport: overtime_waiting_fee_per_hour /
 *       그 외: overtime_fee_per_hour). 생략 시 기본 overtime_fee_per_hour.
 *     - overtime: (deprecated) 한국어 사전 포맷 문자열. overtimePrice 미존재 시 fallback
 *   showReview: 후기 버튼 표시 여부 (default: true)
 *   showPriceNote: 요금 안내 문구 표시 여부 (default: true)
 *   reviewTitle, reviewRating, priceNote: 후기/요금 커스터마이징
 */
export default function VehicleInfoSection({
  images,
  carName,
  usageTimeBadge,
  specs,
  waitInfo,
  surcharges,
  showReview = true,
  showPriceNote = true,
  reviewTitle,
  priceNote,
}) {
  const { t } = useTranslation();
  const money = useMoney();
  const resolvedReviewTitle = reviewTitle || t('common.vehicle_review_title_default');
  const resolvedPriceNote = priceNote || t('common.price_note_default');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [avgRating, setAvgRating] = useState('');
  useEffect(() => {
    if (!showReview) return;
    reviewService.getStats().then((res) => {
      if (res.average_rating != null) setAvgRating(res.average_rating);
    }).catch(() => {});
  }, [showReview]);

  // 백엔드(dummy) 한국어 텍스트를 렌더 시점에 DeepL로 번역
  const specTexts = useMemo(() => (specs || []).map((s) => s?.text || ''), [specs]);
  const freeRowLabels = useMemo(
    () => (waitInfo?.freeRows || []).map((r) => r?.label || ''),
    [waitInfo?.freeRows]
  );
  const freeRowValues = useMemo(
    () => (waitInfo?.freeRows || []).map((r) => r?.value || ''),
    [waitInfo?.freeRows]
  );
  const i18nBundle = useMemo(
    () => [
      carName || '',
      usageTimeBadge || '',
      waitInfo?.overtime || '',
      ...specTexts,
      ...freeRowLabels,
      ...freeRowValues,
      ...(surcharges || []),
    ],
    [carName, usageTimeBadge, waitInfo?.overtime, specTexts, freeRowLabels, freeRowValues, surcharges]
  );
  const translatedBundle = useDeepLTranslated(i18nBundle);
  const tCarName = translatedBundle[0] || carName;
  const tUsageBadge = translatedBundle[1] || usageTimeBadge;
  // overtimePrice(raw 숫자) 우선 → useMoney 로 통화 변환 + i18n 템플릿.
  // 라벨은 overtimeI18nKey 지정값 우선 (airport: 'overtime_waiting_fee_per_hour',
  // 그 외: 'overtime_fee_per_hour'). 없으면 legacy overtime 문자열을 DeepL 폴백.
  const tOvertime = (waitInfo?.overtimePrice != null && Number(waitInfo.overtimePrice) > 0)
    ? t(waitInfo.overtimeI18nKey || 'booking.overtime_fee_per_hour', { amount: money(Number(waitInfo.overtimePrice)) })
    : (translatedBundle[2] || waitInfo?.overtime);
  const specOffset = 3;
  const freeLabelOffset = specOffset + specTexts.length;
  const freeValueOffset = freeLabelOffset + freeRowLabels.length;
  const surchargeOffset = freeValueOffset + freeRowValues.length;
  const tSpecs = (specs || []).map((s, i) => ({ ...s, text: translatedBundle[specOffset + i] || s.text }));
  // BugHerd #86: "60분 무료 대기" 가 외국어 모드에서도 한국어로 노출되던 문제.
  // value 에서 분 단위 숫자 추출 후 정적 i18n key 로 직접 번역. DeepL 실패/캐시 미스
  // 영향을 없앰 (이전엔 DeepL 결과가 빈 값이면 한국어 fallback).
  // #225: 라벨도 direction 별 정적 i18n 키로 번역 (DeepL 의 "샌딩→sanding" 오역 회피).
  //   사내 표준: 공항 픽업 = "Airport Pickup" / 공항 샌딩 = "Airport Drop-off" (termsDataI18n 와 동일).
  const FREE_LABEL_I18N = { pickup: 'booking.free_waiting_pickup', sending: 'booking.free_waiting_sending' };
  const tFreeRows = (waitInfo?.freeRows || []).map((r, i) => {
    const m = String(r?.value || '').match(/(\d+)/);
    const minutes = m ? Number(m[1]) : null;
    const i18nValue = minutes != null
      ? t('booking.free_waiting_minutes', { minutes, defaultValue: r.value })
      : (translatedBundle[freeValueOffset + i] || r.value);
    const labelKey = FREE_LABEL_I18N[r?.direction];
    const i18nLabel = labelKey
      ? t(labelKey, { defaultValue: translatedBundle[freeLabelOffset + i] || r.label })
      : (translatedBundle[freeLabelOffset + i] || r.label);
    return {
      ...r,
      label: i18nLabel,
      value: i18nValue,
    };
  });
  const tSurcharges = (surcharges || []).map((s, i) => translatedBundle[surchargeOffset + i] || s);

  return (
    <>
      <CarInfoBox
        images={images}
        carName={tCarName}
        usageTimeBadge={tUsageBadge}
        specs={tSpecs}
      >
        {(waitInfo || surcharges) && (
          <div className="flex flex-col gap-1.5">
            {/* 무료 대기 시간 (공항) */}
            {tFreeRows.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-2sm font-medium text-mono-700">{t('common.free_wait_time_label')}</span>
                {tFreeRows.map((row, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <Icon name="time" size={16} className="text-orange-500 shrink-0" />
                    <span className="text-2sm text-mono-600">{row.label}: <span className="font-semibold text-orange-500">{row.value}</span></span>
                  </div>
                ))}
              </div>
            )}
            {/* 초과 요금 */}
            {tOvertime && (
              <div className="flex items-center gap-1.5">
                <Icon name="time" size={16} className="text-mono-500 shrink-0" />
                <span className="text-2sm font-medium text-mono-700">{tOvertime}</span>
              </div>
            )}
            {/* 야간/주말 할증 */}
            {tSurcharges.map((s, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <Icon name="info" size={18} className="text-mono-500 shrink-0" />
                <span className="text-2sm font-medium text-orange-500">{s}</span>
              </div>
            ))}
          </div>
        )}
      </CarInfoBox>

      {showReview && (
        <ReviewButton
          title={resolvedReviewTitle}
          rating={avgRating}
          onClick={() => setShowReviewModal(true)}
        />
      )}

      {showPriceNote && (
        <p className="text-2sm font-medium text-orange-500">{resolvedPriceNote}</p>
      )}

      {showReviewModal && (
        <ReviewListModal onClose={() => setShowReviewModal(false)} />
      )}
    </>
  );
}
